import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  ConversationUpdatedSubscription,
  ConversationUpdateType,
  useConversationUpdatedSubscription,
  useGetConversationExternalMessagesQuery,
} from '@shared/generated/graphql';
import { toConversationMessageEvents } from '@shared/graphql/fromFragments/externalMessage';
import { useConversation } from '@shared/graphql/hooks/conversations/useConversation';
import { useEffect, useRef } from 'react';

const CONVERSATION_MESSAGE_UPDATE_REFETCH_DELAY_MS = 1000;

export function useConversationMessages(
  conversationId: string,
  options?: { includeMirrored?: boolean; pollIntervalMs?: number }
) {
  const pollIntervalMs = options?.pollIntervalMs ?? 2 * 1000;
  const { defaultOrgId } = useMeContext();
  const { conversation } = useConversation({
    id: conversationId,
    pollIntervalMs,
  });
  // NOTE(wassim): Messages are seeded into the cache when sent, and updates
  // for a thread that isn't mounted are missed, so serve the cache immediately
  // but always refetch on mount instead of waiting for the next poll.
  const { data, error, loading, refetch } =
    useGetConversationExternalMessagesQuery({
      variables: {
        input: {
          conversationId: conversationId,
          includeMirrored: options?.includeMirrored ?? false,
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      pollInterval: pollIntervalMs,
      skip: !conversationId,
    });
  const historyFreshnessRef = useRef({ conversationId, isFresh: false });
  if (historyFreshnessRef.current.conversationId !== conversationId) {
    historyFreshnessRef.current = { conversationId, isFresh: false };
  }
  if (!loading && !error) {
    historyFreshnessRef.current.isFresh = true;
  }
  const conversationIdRef = useRef(conversationId);
  const refetchRef = useRef(refetch);
  const refetchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  conversationIdRef.current = conversationId;
  refetchRef.current = refetch;

  useConversationUpdatedSubscription({
    skip: !defaultOrgId || !conversationId,
    variables: { organizationId: defaultOrgId ?? '' },
    onData: ({ data: subscriptionData }) => {
      const conversationUpdate = subscriptionData.data?.conversationUpdated;
      if (
        !shouldRefetchConversationMessages(
          conversationUpdate,
          conversationIdRef.current
        )
      ) {
        return;
      }
      if (refetchTimeout.current !== undefined) {
        return;
      }
      refetchTimeout.current = setTimeout(() => {
        refetchTimeout.current = undefined;
        refetchRef.current().catch(console.error);
      }, CONVERSATION_MESSAGE_UPDATE_REFETCH_DELAY_MS);
    },
  });

  useEffect(
    () => () => {
      clearScheduledConversationMessageRefetch(refetchTimeout);
    },
    [conversationId, defaultOrgId]
  );

  return {
    conversationCreatedAt: conversation?.createdAt,
    msgs: toConversationMessageEvents(data),
    isHistoryStale: !historyFreshnessRef.current.isFresh,
    historyError: error,
    events: conversation?.events ?? [],
  };
}

function clearScheduledConversationMessageRefetch(refetchTimeout: {
  current: ReturnType<typeof setTimeout> | undefined;
}): void {
  if (refetchTimeout.current === undefined) {
    return;
  }
  clearTimeout(refetchTimeout.current);
  refetchTimeout.current = undefined;
}

function shouldRefetchConversationMessages(
  conversationUpdate:
    | ConversationUpdatedSubscription['conversationUpdated']
    | undefined,
  conversationId: string
): boolean {
  return (
    conversationUpdate?.conversationId === conversationId &&
    (conversationUpdate.type === ConversationUpdateType.MESSAGE_CREATED ||
      conversationUpdate.type === ConversationUpdateType.MESSAGE_UPDATED)
  );
}
